import {
  FluidAlignment,
  FluidDisplayDensity,
  FluidElementState,
  FluidElementType,
  FluidKeyedObject,
  FluidMenuItem,
  FluidMenuState,
  FluidTheme,
  FluidThemeInterface,
  FluidThemePalette,
  HTMLFluidHeaderElement,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { createPopper, Instance, Placement } from '@popperjs/core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  forceUpdate,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Subscription } from 'rxjs';

import {
  CONTEXT_BAR_ENTRY,
  HEADER_ENTRY,
  HEADER_INVALID_ALIGNMENT,
} from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { ElementRegistry } from '../../utils/elements/element-registry.utils';
import { ESCAPE, EventUtils } from '../../utils/events/event-utils';
import { togglePopperListeners } from '../../utils/popperjs/popperjs.utils';
import {
  FluidMediaState,
  ResponsiveUtils,
} from '../../utils/resize/responsive-utils';
import { Utils } from '../../utils/utils';
import {
  HeaderContent,
  HeaderLogo,
  HeaderMobileBtn,
  HeaderPlaceholder,
  HeaderSearch,
} from './components/fluid-header.components';
import { HeaderNavigation } from './components/fluid-header-navigation.components';
import {
  getSelectedItemParent,
  hasHeaderSubmenu,
  hasMegaMenu,
  initializeHeaderState,
} from './utils/fluid-header.utils';

/**
 * @displayName Header
 *
 * @contextData FLUID Header is a key component of any user interface. It helps users quickly and easily find the information they're looking for via links to the main pages & sections of a site. Since it appears on every page, Header facilitates a consistent user experience by allowing users to navigate from one page to another regardless of the content of each page. Header can be combined with FLUID Sidebar for more complex UIs where advanced navigation is needed.
 *
 * @slot header-logo - Named slot for custom logo. When provided, default logo will NOT be displayed.
 * Preferred sizing: Aspect Ratio: 4:1-10:1 | Width: 175px-300px | Height: 20px-60px
 * @slot header-search - Named slot for search bar / site search controls.
 * @slot header-content - Named slot for additional header content (i.e. links, menus, etc.)
 *
 * @builder true
 */
@Component({
  tag: 'fluid-header',
  styleUrl: 'fluid-header.scss',
  shadow: true,
})
export class FluidHeader implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to root <header> container element.
   */
  private _containerRef: HTMLElement;

  /**
   * Reference to inner <nav> element.
   */
  private _navRef: HTMLElement;

  /**
   * Reference to content slot's parent <div>.
   */
  private _contentRef: HTMLElement;

  /**
   * Reference to "placeholder" <div> (fixed-position Header ONLY).
   */
  private _placeholderRef: HTMLElement;

  /**
   * Current height of Header container. Used to validate that Header
   * height has changed before triggering update to sticky elements.
   */
  private _containerHeight: number;

  /**
   * Reference to ResizeObserver instance that monitors the <header>
   * element's size & calls "updateHeaderStructure()" when it changes.
   */
  private _resizeObserver: ResizeObserver;

  /**
   * Reference to the MutationObserver that monitors Context Bar for
   * changes in slotted child content.
   */
  private _slotObserver: MutationObserver;

  /**
   * RxJS subscription to global "media state" (monitors viewport size).
   */
  private _mediaStateSubscription$: Subscription;

  /**
   * When true, indicates that submenu / mega menu PopperJS instances
   * have been successfully initialized OR that submenus do NOT exist
   * & PopperJS does NOT need to be initialized.
   */
  private _poppersInitialized = false;

  /**
   * RxJS subscription to global Element Registry. Allows component to
   * be aware of other "sticky" components' positions within the DOM.
   * Stored as a variable to allow unsubscribing when component is
   * disconnected/removed from DOM.
   */
  private _registrySubscription$: Subscription;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element
   */
  @Element() host: HTMLFluidHeaderElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: { content?: string };
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound = false;

  // ================================================================ //
  // -- States

  /**
   * Derived Header state.
   */
  @State() _headerState: FluidMenuState;

  /**
   * Whether the mobile slide-out menu is visible.
   */
  @State() _mobileMenuOpen = false;

  /**
   * Sets mobile "flyout" menu opacity/visibility to "hidden" when menu
   * is closed (AFTER menu slides out of view). Used to prevent FOUC when
   * Header is first rendered or when viewport size/orientation changes.
   */
  @State() _mobileMenuHidden = true;

  /**
   * Boolean tracking whether the viewport is tablet/mobile size (< 992px)
   * or desktop size (≥ 992px).
   */
  @State() _mobileViewport: boolean;

  /**
   * Whether the consuming device's input method is touch-based.
   */
  @State() _touchDevice: boolean;

  /**
   * Whether the consuming UI has a Page Context Bar instance. Used to
   * remove Header's box-shadow when Context Bar is present underneath.
   */
  @State() _hasContextBar = false;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * When provided, sets the current theme's color palette & appearance.
   * NOTE: a theme palette acts as a "style override" applied ON TOP of
   * the current theme & isn't necessary for "standard" theme aesthetics.
   */
  @Prop({ mutable: true }) themePalette: FluidThemePalette;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Array of FluidMenuItems that populate Header with links / submenus.
   * NOTE: FluidMenuItem provides 2 ways to set a Header link's destination
   * URL: the "href" property & "route" property. Use the "href" property to
   * generate a standard anchor (<a>) element that does NOT rely on the
   * consuming app's router for navigation. Use the "route" property when
   * navigation SHOULD be handled by the consuming app's router (i.e.
   * equivalent to Angular's "routerLink" or React's <Link to="...">).
   */
  @Prop({ mutable: true }) links: FluidMenuItem[];

  @Watch('links')
  handleLinksUpdate() {
    this.removePopperInstances();
    this._headerState = initializeHeaderState(this.links);
  }

  /**
   * Allows configuration of Header's built-in logo, including the ability
   * to make the logo a clickable hyperlink. The config object accepts the
   * following FLUID Link properties: "href" OR "route" (required) &
   * "target" (optional). It also accepts an optional "tooltip" property
   * that displays tooltip text on :hover.
   */
  @Prop({ mutable: true }) logoConfig: Pick<
    FluidMenuItem,
    'href' | 'route' | 'target'
  > & {
    tooltip?: string;
  };

  /**
   * When true, Header will NOT render a logo OR a logo slot.
   */
  @Prop({ mutable: true }) noLogo = false;

  /**
   * When true, Header will be "sticky" when scrolling (i.e. Header will
   * have a fixed position at the top of the viewport).
   */
  @Prop({ mutable: true }) fixedPosition = false;

  @Watch('fixedPosition')
  handleFixedPositionChange() {
    ElementRegistry.updateElementDataStore(
      HEADER_ENTRY,
      { sticky: this.fixedPosition },
      true
    );
  }

  /**
   * Sets alignment of links within Header container (at desktop size).
   * Accepts "left", "center", or "right".
   */
  @Prop({ mutable: true }) alignment: FluidAlignment = FluidAlignment.LEFT;

  @Watch('alignment')
  handleAlignmentChange() {
    this.updatePopperOptions();
  }

  /**
   * When true, Header submenus / mega menus will open on :hover (at
   * desktop size).
   */
  @Prop({ mutable: true }) hoverOpenSubmenus = true;

  /**
   * Sets Header's height, link font size, padding, spacing, etc. to be
   * more or less compact.
   */
  @Prop({ mutable: true }) displayDensity: FluidDisplayDensity =
    FluidDisplayDensity.DEFAULT;

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when a link is clicked. Contains the corresponding
   * FluidMenuItem's "route" or "href" property value. This event only
   * applies to links (i.e. menu items), and is NOT emitted when a
   * submenu/mega menu button is clicked.
   */
  @Event() linkClicked: EventEmitter<string>;

  /**
   * Event fired when a menu item with no submenu / mega menu is clicked.
   * Contains the entire FluidMenuItem object.
   */
  @Event() menuItemClick: EventEmitter<FluidMenuItem>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action setZIndex - Set component z-index relative to global base value
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      init: () => (this._i18nBound = true),
    });
    Utils.setZIndex(this.host);
    this.subscribeToElementRegistry();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action initializeHeaderState - initialize derived Header state
   * @action validateAlignment - ensure that a valid value is passed to "alignment" Prop
   * @action watchMedia - initialize watcher for changes in viewport size & input device
   */
  componentWillLoad(): Promise<void> | void {
    this._headerState = initializeHeaderState(this.links);
    this.validateAlignment();
    this.watchMedia();
    this._slotObserver = Utils.watchChildContent(
      this.host,
      Utils.debounce(() => forceUpdate(this), 100),
      {
        attributes: true,
        attributeFilter: ['slot'],
        childList: true,
        subtree: true,
      }
    );
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action updateHeaderStructure - updates Header's layout & placeholder height
   * @action initializeResizeObserver - begins watching for Header size changes
   * @action createPopperInstances - initializes PopperJS for submenus & mega menus
   * @action addElement - adds Header to global Element Registry
   */
  componentDidLoad() {
    this.updateHeaderStructure();
    this.initializeResizeObserver();
    this.createPopperInstances();
    ElementRegistry.addElement(HEADER_ENTRY, this.host, {
      sticky: this.fixedPosition,
    });
  }

  /**
   * Called when component will be updated due to Prop or State change.
   * @action createPopperInstances - initializes PopperJS for submenus & mega menus
   */
  componentWillUpdate() {
    this.createPopperInstances(); // Only runs if PopperJS is NOT already initialized
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action removeElement - removes Header from global Element Registry
   * @action removePopperInstances - destroy PopperJS instances attached to submenus
   * @action _resizeObserver.disconnect - stop ResizeObserver from watching for changes
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    ElementRegistry.removeElement(HEADER_ENTRY, this.host);

    this._registrySubscription$?.unsubscribe();

    this._mediaStateSubscription$?.unsubscribe();
    delete this._mediaStateSubscription$;

    this.removePopperInstances();
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;

    this._slotObserver?.disconnect();
    this._slotObserver = undefined;

    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Listener for outside clicks (i.e. outside Header). If desktop size,
   * outside click will close all open submenus. If mobile size, outside
   * click will close mobile menu.
   */
  @Listen('click', { target: 'body' })
  onBodyClick(event) {
    if (EventUtils.eventPathIncludes(event, this.host)) return;
    this._mobileMenuOpen ? this.toggleMobileMenu() : this.closeAllSubmenus();
  }

  /**
   * Event listener for "focusOut" events on host. If desktop size, closes
   * all open submenus. Only triggered when ALL Header descendants lose
   * :focus (i.e. instead of triggering on each internal blur event).
   */
  @Listen('focusout')
  onFocusOut() {
    !this._mobileViewport && this.closeAllSubmenus();
  }

  /**
   * Event listener for "keydown" events on host. If the "escape" key is
   * pressed while a submenu is open, the submenu will close & :focus will
   * be transferred to its associated "submenu trigger" button.
   */
  @Listen('keydown', { capture: true })
  onKeyDown(event: KeyboardEvent) {
    if (!EventUtils.isKey(event, ESCAPE)) return;

    Utils.setFocus(
      this._headerState?.submenuItems?.find((item) => item.submenuOpen)
        ?._submenuTrigger
    );
    this.closeAllSubmenus();
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Closes all open submenus & mega menus.
   */
  @Method()
  async closeAllSubmenus(): Promise<void> {
    if (!this._headerState?.submenuItems?.length) return;
    let refresh = false;

    this._headerState.submenuItems.forEach((item) => {
      if (!item.submenuOpen) return;
      item.submenuOpen = false;
      this.togglePopperListeners(item, false);
      refresh = true;
    });

    refresh && this.updateHeaderState();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Subscribes to Element Registry, then watches for Page Context Bar
   * component to be added to the registry.
   */
  subscribeToElementRegistry(): void {
    this._registrySubscription$ = ElementRegistry.registry$.subscribe(
      (registry: FluidKeyedObject<FluidElementState>) => {
        this._hasContextBar = !!registry[CONTEXT_BAR_ENTRY];
      }
    );
  }

  /**
   * If "alignment" Prop is given an invalid value, outputs console
   * warning & resets Prop to default value.
   */
  validateAlignment(): void {
    if (Utils.isFluidEnum<FluidAlignment>(this.alignment, FluidAlignment))
      return;
    Utils.consoleWarn(this.host, HEADER_INVALID_ALIGNMENT(this.alignment));
    this.alignment = FluidAlignment.LEFT;
  }

  /**
   * Initializes ResizeObserver that watches for changes in the size of
   * Header's main container (due to changes in viewport size, theme,
   * Prop values, etc.). More reliable/accurate & less resource-intensive
   * than "window.resize" listener.
   */
  initializeResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') return;

    this._resizeObserver = new ResizeObserver((entries) => {
      if (entries.length !== 1) return;
      this.updateHeaderStructure(entries[0]?.borderBoxSize[0]?.blockSize);
    });
    this._resizeObserver.observe(this._containerRef);
  }

  /**
   * Connects Header to ResponsiveUtils, which queries the viewport to
   * determine the consuming device's size, as well as whether the device's
   * input is touch-based. When the viewport size changes, triggers
   * updates to close/hide the mobile "flyout" menu & add/remove PopperJS
   * instances for submenus & mega menus.
   */
  watchMedia(): void {
    this._mediaStateSubscription$ = ResponsiveUtils.mediaState$.subscribe(
      (mediaState: FluidMediaState) => {
        const { breakpointLg, touchDevice } = mediaState;
        const mobileViewportPrevState = this._mobileViewport;

        this._mobileViewport = !breakpointLg.matches;
        this._touchDevice = touchDevice.matches;

        this.closeAllSubmenus();

        // If viewport was previously desktop size & now is mobile size...
        if (!mobileViewportPrevState && this._mobileViewport) {
          this._mobileMenuHidden = true; // Prevents FOUC
          this.removePopperInstances(); // Removes submenu positioning / edge detection
        }
        // If viewport is currently desktop size...
        else if (!this._mobileViewport) {
          this.toggleMobileMenu(false); // Closes "flyout" menu
          this._mobileMenuHidden = false; // Un-hides div.header-main
          this.createPopperInstances(); // Adds submenu positioning / edge detection
        }
      }
    );
  }

  /**
   * Returns array of "top-level" submenus / mega menus (i.e. menu items
   * with submenus that are NOT nested within a mega menu).
   */
  getTopLevelSubmenus(): FluidMenuItem[] {
    if (!this._headerState?.submenuItems?.length) return;
    return this._headerState.submenuItems.filter(
      (item) => item._submenuDepth === 1
    );
  }

  /**
   * Determines alignment of submenu / mega menu to its "trigger" button.
   * If "alignment" Prop is "center" or "right", horizontally centers mega
   * menu underneath its "trigger" button - otherwise, aligns left edge
   * of submenu / mega menu to left edge of "trigger" button.
   * @param menuItem - menu item containing the menu to align
   */
  getPopperPlacement(menuItem: FluidMenuItem): Placement {
    return hasMegaMenu(menuItem) && this.alignment !== FluidAlignment.LEFT
      ? 'bottom'
      : 'bottom-start';
  }

  /**
   * Initializes PopperJS instances for each submenu / mega menu. Only
   * runs at desktop viewport size (unnecessary at mobile/tablet sizes).
   */
  createPopperInstances(): void {
    if (this._poppersInitialized || this._mobileViewport) return;
    const topLevelSubmenus = this.getTopLevelSubmenus();

    topLevelSubmenus?.forEach((menuItem) => {
      if (
        !!menuItem._popperInstance ||
        !menuItem._submenuTrigger ||
        !menuItem._submenuElement
      )
        return;

      menuItem._popperInstance = createPopper(
        menuItem._submenuTrigger,
        menuItem._submenuElement,
        {
          strategy: 'absolute',
          placement: this.getPopperPlacement(menuItem),
          modifiers: [
            {
              name: 'eventListeners',
              enabled: !!menuItem.submenuOpen,
            },
            {
              name: 'preventOverflow',
              options: { padding: 16 },
            },
            {
              name: 'offset',
              options: {
                offset: ({ placement, popper }) =>
                  hasMegaMenu(menuItem) && placement === 'bottom-start'
                    ? [-(popper.width / 6), 8]
                    : [0, 8],
              },
            },
          ],
        }
      );
    });

    // Mark initialized if all submenus have PopperJS instances OR no submenus exist
    this._poppersInitialized =
      !topLevelSubmenus ||
      topLevelSubmenus.every((menuItem) => !!menuItem._popperInstance);
  }

  /**
   * Updates PopperJS instance when "placement" Prop changes.
   */
  updatePopperOptions(): void {
    if (this._mobileViewport || !this._poppersInitialized) return;

    this.getTopLevelSubmenus()?.forEach((menuItem) => {
      (menuItem._popperInstance as Instance)?.setOptions((options) => ({
        ...options,
        placement: this.getPopperPlacement(menuItem),
        modifiers: [...options.modifiers],
      }));
    });
  }

  /**
   * Removes PopperJS instances from all submenus / mega menus. Called
   * when viewport size changes from desktop => mobile OR on
   * "disconnectedCallback()".
   */
  removePopperInstances(): void {
    this.getTopLevelSubmenus()?.forEach((menuItem) => {
      (menuItem._popperInstance as Instance)?.destroy();
      menuItem._popperInstance = undefined;
    });
    this._poppersInitialized = false;
  }

  /**
   * Toggles submenu / mega menu PopperJS event listeners on/off.
   * @param menuItem - menu item containing PopperJS instance to toggle
   * @param enabled - toggles event listeners on if true, off if false
   */
  togglePopperListeners(menuItem: FluidMenuItem, enabled: boolean): void {
    if (!menuItem._popperInstance) return;
    togglePopperListeners(menuItem._popperInstance, enabled);
  }

  /**
   * Triggers update to Header's layout & placeholder height when <header>
   * element's height changes. Essentially acts as a "throttle" function
   * for Header's ResizeObserver, as it only triggers updates if the
   * component's height has changed.
   * @param containerHeight - the current height of the <header> element
   */
  updateHeaderStructure(
    containerHeight: number = this._containerRef?.offsetHeight
  ): void {
    if (containerHeight === this._containerHeight) return;

    this._containerHeight = containerHeight;
    this.setLayout();
    this.setPlaceholderHeight();
    ElementRegistry.updateRegistry();
  }

  /**
   * Rearranges Header layout to prevent "header-content" slot wrapping
   * underneath navigation links (i.e. if width of slotted content
   * overflows flexbox row).
   */
  setLayout(): void {
    if (isNullOrUndefined(this._navRef) || isNullOrUndefined(this._contentRef))
      return;

    if (!this._mobileViewport) {
      const navRect = this._navRef.getBoundingClientRect();
      const contentRect = this._contentRef.getBoundingClientRect();
      this._navRef.style.order = contentRect.top !== navRect.top ? '1' : '';
    } else {
      this._navRef.style.order = '';
    }
  }

  /**
   * If "fixedPosition" Prop is true, sets "placeholder" element height
   * equal to height of the <header> element to offset Header's position
   * in the DOM.
   */
  setPlaceholderHeight(): void {
    if (!this.fixedPosition) return;
    this._placeholderRef.style.height = `${this._containerHeight}px`;
  }

  /**
   * Opens/closes "flyout" menu & disables background scroll at tablet/
   * mobile viewport size. If menu is open, removes visibility/opacity
   * styles that hide it when closed.
   */
  toggleMobileMenu(open = !this._mobileMenuOpen): void {
    if (this._mobileMenuOpen === open) return;
    this._mobileMenuOpen = open;

    if (this._mobileMenuOpen) {
      this._mobileMenuHidden = false;
      Utils.disableBodyScroll();
    } else {
      Utils.enableBodyScroll();
    }
  }

  /**
   * Sets Header's mobile "flyout" menu to "display: none" when closed
   * (AFTER "fade out" animation ends). Prevents FOUC
   */
  hideMobileMenu(event: TransitionEvent): void {
    if (
      this._mobileMenuOpen ||
      !this._mobileViewport ||
      event.propertyName !== 'transform'
    )
      return;
    this._mobileMenuHidden = true;
  }

  /**
   * Closes all open submenus, toggles given submenu or mega menu open/
   * closed, & enables/disables PopperJS event listeners attached to it.
   * Called each time a click, mouseEnter/mouseLeave, or :focus event
   * occurs on a submenu trigger element (UNLESS the menu item is disabled).
   * @param menuItem - FluidMenuItem containing the submenu to toggle
   * @param open - true if submenu should open, false if it should close
   */
  toggleSubmenu(menuItem: FluidMenuItem, open = !menuItem.submenuOpen): void {
    if (
      menuItem.disabled ||
      menuItem.submenuOpen === open ||
      (!hasHeaderSubmenu(menuItem) && !hasMegaMenu(menuItem))
    )
      return;

    !menuItem.submenuOpen && this.closeAllSubmenus();
    menuItem.submenuOpen = open;
    this.togglePopperListeners(menuItem, open);
    this.updateHeaderState();
  }

  /**
   * Prevents the default behavior of anchor-based menu items on click.
   * Used when app's router is used for navigation (i.e. FluidMenuItem
   * "route" property is used instead of "href") OR menu item is disabled.
   * @param menuItem - the menu item that was clicked
   * @param event - the click event
   */
  preventLinkDefault(menuItem: FluidMenuItem, event: MouseEvent): void {
    if (
      menuItem.disabled ||
      (!menuItem.href && menuItem.elementType === FluidElementType.ANCHOR)
    ) {
      event.preventDefault();
    }
  }

  /**
   * Updates Header's selected item & selected item parent, closes all
   * open submenus, then emits "linkClicked" event containing the "href"
   * or "route" of the selected menu item. Called each time a menu item
   * is clicked, regardless of if the menu item is "top-level" or nested
   * within a submenu.
   * @param menuItem - the FluidMenuItem that was clicked
   * @param event - the click event
   */
  handleLinkClick(menuItem: FluidMenuItem, event: MouseEvent) {
    this.preventLinkDefault(menuItem, event);
    if (menuItem.disabled) return;

    if (this._headerState.selectedItem) {
      this._headerState.selectedItem.selected = false;
    }
    menuItem.selected = true;
    this._headerState.selectedItem = menuItem;
    this._headerState.selectedItemParent = getSelectedItemParent(
      this._headerState.submenuItems,
      menuItem
    );
    this.closeAllSubmenus();

    this.linkClicked.emit(menuItem.route ?? menuItem.href);
    this.menuItemClick.emit(menuItem);
    this.toggleMobileMenu(false);
    this.updateHeaderState();
  }

  /**
   * Simple utility function that updates "_headerState" object reference
   * & triggers a component re-render.
   */
  updateHeaderState(): void {
    this._headerState = { ...this._headerState };
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host>
        <header
          ref={(el) => (this._containerRef = el as HTMLElement)}
          style={{ 'z-index': `${this.zIndex}` }}
          class={{
            [`fluid-header theme-${this.theme}`]: true,
            [`display-density-${this.displayDensity}`]: true,
            ['disable-shadow']: !!this._hasContextBar,
            [`theme-palette-${this.themePalette}`]: !!this.themePalette,
            'header-fixed': this.fixedPosition,
          }}>
          <HeaderMobileBtn host={this} />
          <HeaderLogo host={this} position='mobile' />
          <div
            key='header-main'
            onTransitionEnd={(event) => this.hideMobileMenu(event)}
            class={{
              'header-main': true,
              'header-mobile-open': this._mobileMenuOpen,
              'header-mobile-hidden': this._mobileMenuHidden,
            }}
            style={
              !!this._mobileViewport && {
                height: `calc(100vh - ${this._containerHeight}px)`,
                top: `${this._containerHeight}px`,
              }
            }>
            <HeaderLogo host={this} position='desktop' />
            <HeaderNavigation
              host={this}
              i18nStore={this._i18nStore}
              headerState={this._headerState}
              setRef={(el) => (this._navRef = el)}
            />
            <HeaderSearch host={this} />
            <HeaderContent setRef={(el) => (this._contentRef = el)} />
          </div>
        </header>
        <HeaderPlaceholder
          host={this}
          containerHeight={this._containerHeight}
          setRef={(el) => (this._placeholderRef = el)}
        />
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Causes Header to display FLUID logo instead of iUW or LM logos.
   * @internal
   */
  @Prop({ mutable: true }) fluidLogo: boolean;

  /**
   * Overrides default CSS z-index value for Header root & "main" container.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;
}
